/**
 * Razorpay Checkout helper — loads the Checkout SDK once and opens the modal.
 *
 * Usage:
 *   import { openRazorpayCheckout } from "@/lib/razorpay";
 *   await openRazorpayCheckout({ order_id, key_id, amount_paise, name, prefill, onSuccess });
 *
 * The caller is responsible for creating the server-side order via
 *   POST /api/payments/order         (wallet top-up)
 *   POST /api/payments/report-order  (per-report unlock)
 * and calling POST /api/payments/verify with the signature returned by the modal.
 */
let _rzpScriptPromise = null;

function loadRazorpayScript() {
  if (typeof window === "undefined") return Promise.reject(new Error("no window"));
  if (window.Razorpay) return Promise.resolve();
  if (_rzpScriptPromise) return _rzpScriptPromise;
  _rzpScriptPromise = new Promise((resolve, reject) => {
    const s = document.createElement("script");
    s.src = "https://checkout.razorpay.com/v1/checkout.js";
    s.async = true;
    s.onload = () => resolve();
    s.onerror = () => {
      _rzpScriptPromise = null;
      reject(new Error("Failed to load Razorpay Checkout script"));
    };
    document.head.appendChild(s);
  });
  return _rzpScriptPromise;
}

/**
 * Open the Razorpay Checkout modal.
 *
 * @param {Object}  opts
 * @param {string}  opts.key_id          — Razorpay public key
 * @param {string}  opts.order_id        — Razorpay order id from /payments/order
 * @param {number}  opts.amount_paise    — Amount in paise
 * @param {string}  [opts.name]          — Merchant name shown at top of modal
 * @param {string}  [opts.description]   — Payment description
 * @param {string}  [opts.image]         — Merchant logo URL
 * @param {Object}  [opts.prefill]       — {name, email, contact}
 * @param {string}  [opts.theme_color]   — Hex color for the checkout theme
 * @param {Function} opts.onSuccess      — Called with {razorpay_order_id, razorpay_payment_id, razorpay_signature}
 * @param {Function} [opts.onDismiss]    — Called when the user closes the modal
 */
export async function openRazorpayCheckout(opts) {
  await loadRazorpayScript();
  return new Promise((resolve, reject) => {
    const options = {
      key: opts.key_id,
      amount: opts.amount_paise,
      currency: opts.currency || "INR",
      order_id: opts.order_id,
      name: opts.name || "EFGH — Everything For Good Health",
      description: opts.description || "Payment",
      image: opts.image || "/efgh-logo.png",
      prefill: opts.prefill || {},
      theme: { color: opts.theme_color || "#0B1628" },
      handler: (res) => {
        try {
          opts.onSuccess?.(res);
          resolve(res);
        } catch (e) {
          reject(e);
        }
      },
      modal: {
        ondismiss: () => {
          opts.onDismiss?.();
          resolve(null);
        },
      },
    };
    const rzp = new window.Razorpay(options);
    rzp.on("payment.failed", (err) => {
      opts.onFailed?.(err);
      reject(new Error(err?.error?.description || "Payment failed"));
    });
    rzp.open();
  });
}
